import React, { useEffect, useState } from 'react';
import { Box, Text, VStack, HStack, Heading, Button, Center, Link, Spinner } from '@chakra-ui/react';
import API from '../api/axiosConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaApple, FaGoogle } from 'react-icons/fa';
import ICalendarLink from "react-icalendar-link";
import '../styles/pages/ThankYouPage.css';

const ThankYouPage = () => {
    const [calendarInfo, setCalendarInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const rescheduleSuccess = location.state?.rescheduleSuccess;

    useEffect(() => {
        const fetchCalendarInfo = async () => {
            const bookingResponse = location.state?.bookingResponse;
            if (!bookingResponse) {
                navigate('/dashboard');
                return;
            }
            try {
                const response = await API.post('/appointments/web/calendar/info', {
                    data: {
                        _rrd: {
                            _err: bookingResponse._ad._err,
                            _irr: bookingResponse._ad._irr,
                            _tag: bookingResponse._ad._tag
                        }
                    }
                });

                setCalendarInfo(response.data.calendar);
                setLoading(false);
            } catch (error) {
                setError('Failed to fetch calendar info.');
                setLoading(false);
            }
        };

        fetchCalendarInfo();

        // Trigger confetti animation
        const timer = setTimeout(createConfetti, 100);
        return () => clearTimeout(timer);
    }, [location, navigate]);

    const createConfetti = () => {
        const colors = ['#4a90e2', '#74b9ff', '#a8daff', '#ffffff'];
        for (let i = 0; i < 50; i++) {
            setTimeout(() => {
                const confetti = document.createElement('div');
                confetti.className = 'confetti';
                confetti.style.left = Math.random() * 100 + '%';
                confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
                confetti.style.animationDelay = Math.random() * 3 + 's';
                confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
                document.body.appendChild(confetti);
                
                setTimeout(() => confetti.remove(), 5000);
            }, i * 30);
        }
    };

    if (loading) {
        return (
            <Box className="thank-you-wrapper">
                <Center className="loading-center">
                    <Spinner size="xl" color="white" />
                    <Text className="loading-text">Fetching appointment details...</Text>
                </Center>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="thank-you-wrapper">
                <Center className="error-center">
                    <Text className="error-text">{error}</Text>
                </Center>
            </Box>
        );
    }

    const extractDateTime = (dateString) => {
        const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (!match) return null;
        const [, year, month, day, hours, minutes] = match.map(Number);
        return { year, month: month - 1, day, hours, minutes };
    };

    const startDateComponents = extractDateTime(calendarInfo.apple.startDate);
    const endDateComponents = extractDateTime(calendarInfo.apple.endDate);

    const startDate = new Date(startDateComponents.year, startDateComponents.month, startDateComponents.day, startDateComponents.hours, startDateComponents.minutes);
    const endDate = new Date(endDateComponents.year, endDateComponents.month, endDateComponents.day, endDateComponents.hours, endDateComponents.minutes);

    const appleEvent = calendarInfo ? {
        title: calendarInfo.apple.title,
        description: calendarInfo.apple.description,
        location: calendarInfo.apple.location,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString()
    } : null;

    const googleStartDate = startDate.toISOString().replace(/-|:|\.\d\d\d/g, "");
    const googleEndDate = endDate.toISOString().replace(/-|:|\.\d\d\d/g, "");

    return (
        <Box className="thank-you-wrapper">
            <Box className="thank-you-container">
                <Box className="success-card">
                    <div className="success-icon-container">✓</div>
                    
                    <Heading as="h1" className="success-title">
                        {rescheduleSuccess ? 'Rescheduled!' : 'You\'re All Set!'}
                    </Heading>
                    
                    <Text className="success-subtitle">
                        Your appointment has been confirmed. We'll send you a reminder 24 hours before your visit.
                    </Text>

                    {calendarInfo && (
                        <VStack spacing={4} align="stretch">
                            <Box className="appointment-details">
                                <Text className="calendar-description">
                                    {calendarInfo.apple.description}
                                </Text>
                            </Box>

                            <Box className="calendar-section">
                                <Text className="calendar-title">Add to your calendar</Text>
                                <HStack className="calendar-buttons" justify="center" flexWrap="wrap">
                                    <ICalendarLink event={appleEvent}>
                                        <Button className="calendar-btn" leftIcon={<FaApple />}>
                                            Apple Calendar
                                        </Button>
                                    </ICalendarLink>
                                    <Button
                                        as={Link}
                                        href={`https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calendarInfo.google.title)}&dates=${googleStartDate}/${googleEndDate}&details=${encodeURIComponent(calendarInfo.google.details)}&location=${encodeURIComponent(calendarInfo.google.location)}`}
                                        className="calendar-btn"
                                        leftIcon={<FaGoogle />}
                                        isExternal
                                    >
                                        Google Calendar
                                    </Button>
                                </HStack>
                            </Box>

                            <Box className="action-buttons">
                                <Button className="primary-btn" onClick={() => navigate('/dashboard')}>
                                    View Dashboard
                                </Button>
                                <Button className="secondary-btn" onClick={() => navigate('/book-appointment')}>
                                    Book Another
                                </Button>
                            </Box>
                        </VStack>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ThankYouPage;